import { getApiUrl } from "./config.js";
import { capturePostHogEvent } from "@/lib/posthog";
import { toErrorDisplayMessage } from "@/lib/utils";

const base = () => getApiUrl().replace(/\/$/, "");

/**
 * A failed call, as every caller sees it. `status: 0` is reserved for "the request never
 * produced a response" — the only case where `error.message` is written by this file rather
 * than by the API.
 */
export type ApiFailure = { ok: false; status: number; error: unknown };

/** Success or failure, discriminated on `ok` — the shape every hook's selector consumes. */
export type ApiResult<T> = { ok: true; data: T } | ApiFailure;

function networkErrorMessage(baseUrl: string): string {
  return `Verbindung zur API fehlgeschlagen. Bitte prüfe deine Internetverbindung und ob die API erreichbar ist. (API: ${baseUrl})`;
}

/**
 * Fehlermeldung für Toast/UI — **immer ein String**.
 * Backend kann `message` als Objekt liefern; rohe Objekte in Sonner/React → „object to primitive“ / invalid child.
 */
export function getApiErrorMessage(
  res: ApiFailure,
  fallback: string
): string {
  if (res.status === 0 && res.error && typeof res.error === "object" && res.error !== null && "message" in res.error) {
    const raw = (res.error as { message: unknown }).message;
    const s = toErrorDisplayMessage(raw);
    if (s) return s;
  }
  const err = res.error as { error?: { message?: unknown }; message?: unknown } | undefined;
  const msg = err?.error?.message ?? err?.message;
  const s = toErrorDisplayMessage(msg);
  return s || fallback;
}

/**
 * The `code` out of the API's error envelope — `{ok: false, error: {code, message, retryable}}`.
 *
 * Lives beside `getApiErrorMessage` because it reads the same envelope one field over, and
 * because the paywall hooks each had their own inline copy of this cast. The gate decision
 * (`403` + `premium_required` / `team_required`) is what turns a failure into an upgrade offer,
 * so the two hooks that make it now read the code through one function rather than two.
 */
export function getApiErrorCode(res: ApiFailure): string | null {
  const code = (res.error as { error?: { code?: unknown } } | undefined)?.error?.code;
  return typeof code === "string" ? code : null;
}

export async function apiGet<T>(path: string, token: string): Promise<ApiResult<T>> {
  const baseUrl = base();
  try {
    const r = await fetch(`${baseUrl}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    const data = await r.json().catch(() => ({}));
    if (r.ok) return { ok: true, data: data as T };
    return { ok: false, status: r.status, error: data };
  } catch {
    return { ok: false, status: 0, error: { message: networkErrorMessage(baseUrl) } };
  }
}

export async function apiPost<T>(path: string, token: string, body: unknown): Promise<ApiResult<T>> {
  const baseUrl = base();
  try {
    const r = await fetch(`${baseUrl}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const data = await r.json().catch(() => ({}));
    if (r.ok) return { ok: true, data: data as T };
    return { ok: false, status: r.status, error: data };
  } catch {
    return { ok: false, status: 0, error: { message: networkErrorMessage(baseUrl) } };
  }
}

export async function apiPatch<T>(path: string, token: string, body: unknown): Promise<ApiResult<T>> {
  const baseUrl = base();
  try {
    const r = await fetch(`${baseUrl}${path}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const data = await r.json().catch(() => ({}));
    if (r.ok) return { ok: true, data: data as T };
    return { ok: false, status: r.status, error: data };
  } catch {
    return { ok: false, status: 0, error: { message: networkErrorMessage(baseUrl) } };
  }
}

export async function apiDelete(path: string, token: string): Promise<{ ok: true } | ApiFailure> {
  const baseUrl = base();
  try {
    const r = await fetch(`${baseUrl}${path}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    const data = await r.json().catch(() => ({}));
    if (r.ok) return { ok: true };
    return { ok: false, status: r.status, error: data };
  } catch {
    return { ok: false, status: 0, error: { message: networkErrorMessage(baseUrl) } };
  }
}

export async function apiDeleteWithBody<T>(path: string, token: string, body: unknown): Promise<{ ok: true; data?: T } | ApiFailure> {
  const baseUrl = base();
  try {
    const r = await fetch(`${baseUrl}${path}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const data = await r.json().catch(() => ({}));
    if (r.ok) return { ok: true, data: data as T };
    return { ok: false, status: r.status, error: data };
  } catch {
    return { ok: false, status: 0, error: { message: networkErrorMessage(baseUrl) } };
  }
}

export function getCrmConnectStartUrl(provider: string): string {
  return `${base()}/crm/connect/${provider}/start`;
}

export function getCrmConnectCallbackPath(provider: string): string {
  return `/crm/connect/${provider}/callback`;
}

/** Fire analytics event (auth optional). Backend immer; PostHog nur bei Einwilligung (capturePostHogEvent prüft intern). */
export async function trackEvent(
  eventName: string,
  token: string | null,
  properties?: Record<string, unknown>
): Promise<void> {
  try {
    await fetch(`${base()}/analytics/events`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ event: eventName, properties: properties ?? {} }),
    });
    capturePostHogEvent(eventName, properties ?? undefined);
  } catch {
    // best-effort
  }
}
